/**
 * MUSKLE member features — save (account metafield via app proxy) + notify (foundation UI).
 * Boot once per page. Duplicate <script> tags must not refetch saved-products or rebind listeners.
 */
(function () {
  'use strict';

  if (window.__MUSKLE_MEMBER_FEATURES_BOOTED__) return;
  window.__MUSKLE_MEMBER_FEATURES_BOOTED__ = true;

  var PROXY_PATH = '/apps/muskle-points/saved-products';
  var PENDING_SAVE_KEY = 'muskle:pending-save';
  var savedIds = new Set();
  var savedHandles = new Set();
  var backendReady = null;
  var hubRenderToken = 0;
  var savedStateObserver = null;
  var resyncScheduled = false;
  var savedLoadGeneration = 0;
  var savedMutationGeneration = 0;
  var savedProductsFetchInFlight = null;

  window.MuskleMemberFeatures = window.MuskleMemberFeatures || {};

  function confirmMemberSessionLoggedIn() {
    document.documentElement.dataset.muskleMemberSessionConfirmed = 'true';
    if (window.MuskleAccountSession && typeof window.MuskleAccountSession.applyStorefrontState === 'function') {
      window.MuskleAccountSession.applyStorefrontState(true);
      syncPointsSessionUi();
      return;
    }
    document.dispatchEvent(
      new CustomEvent('muskle:member-session-confirmed', { detail: { loggedIn: true } })
    );
    syncPointsSessionUi();
  }

  function syncPointsSessionUi() {
    document.querySelectorAll('.muskle-product-card-points--guest').forEach(function (el) {
      el.classList.remove('muskle-product-card-points--guest');
    });
  }

  function isEnglish() {
    var lang = (document.documentElement.lang || '').toLowerCase();
    return lang.indexOf('en') === 0;
  }

  var COPY = isEnglish()
    ? {
        save: 'Save',
        saved: 'Saved',
        loginPrompt: 'Log in to save products.',
        loginLink: 'Log in',
        removeSaved: 'Remove',
        viewProduct: 'View product',
        hubEmpty: 'You have not saved any products yet.',
        saveError: 'Could not save this product right now. Please try again later.',
        unsaveError: 'Could not update saved products right now.',
        notifyTitle: 'Notify me',
        notifyEyebrow: 'BACK IN STOCK?',
        notifyIntro: 'Enter your email. We will send one message when this variant is available again.',
        notifySubmit: 'Keep me updated',
        notifyPrivacy: 'No spam. Only a message for this stock alert.',
        notifyInvalidEmail: 'Please enter a valid email address.',
        notifyError: 'Something went wrong. We could not save your stock alert. Please try again.',
        notifyProviderPending:
          'Stock alerts are moving to a dedicated back-in-stock system. This request was not saved yet. Please try again after that system is connected.',
        notifyAlreadyAvailable: 'Good news — this variant is already back in stock.',
        notifySuccessEyebrow: 'REQUEST RECEIVED',
        notifySuccessTitle: "You're on the list",
        notifySuccessFooter: "You don't need to do anything else.",
        notifyDupEyebrow: "YOU'RE ALREADY ON THE LIST",
        notifyDupTitle: 'We already have your request',
        notifyDupBody: "We'll notify you when this variant is available again.",
      }
    : {
        save: 'Bewaar',
        saved: 'Bewaard',
        loginPrompt: 'Log in om producten te bewaren.',
        loginLink: 'Inloggen',
        removeSaved: 'Verwijder',
        viewProduct: 'Bekijk product',
        hubEmpty: 'Je hebt nog geen producten bewaard.',
        saveError: 'Dit product kon nu niet worden bewaard. Probeer het later opnieuw.',
        unsaveError: 'Opgeslagen producten konden nu niet worden bijgewerkt.',
        notifyTitle: 'Laat mij weten',
        notifyEyebrow: 'WEER OP VOORRAAD?',
        notifyIntro:
          'Vul je e-mailadres in. We sturen je één bericht zodra deze variant weer beschikbaar is.',
        notifySubmit: 'Houd mij op de hoogte',
        notifyPrivacy: 'Geen spam. Alleen een bericht voor deze voorraadmelding.',
        notifyInvalidEmail: 'Vul een geldig e-mailadres in.',
        notifyError:
          'Er ging iets mis. We konden je voorraadmelding niet opslaan. Probeer het opnieuw.',
        notifyProviderPending:
          'Voorraadmeldingen verhuizen naar een apart back-in-stock systeem. Deze aanvraag is nog niet opgeslagen. Probeer het opnieuw zodra dat systeem is aangesloten.',
        notifyAlreadyAvailable: 'Goed nieuws — deze variant is ondertussen weer op voorraad.',
        notifySuccessEyebrow: 'AANVRAAG ONTVANGEN',
        notifySuccessTitle: 'Je staat op de lijst',
        notifySuccessFooter: 'Je hoeft verder niets te doen.',
        notifyDupEyebrow: 'JE STAAT AL OP DE LIJST',
        notifyDupTitle: 'We hebben je aanvraag al ontvangen',
        notifyDupBody: 'Je krijgt bericht zodra deze variant weer beschikbaar is.',
      };

  function isTruthy(val) {
    return val === true || val === 'true';
  }

  function escapeHtml(str) {
    return String(str ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function currentReturnPath() {
    return window.location.pathname + window.location.search;
  }

  function sameProductPath(a, b) {
    var na = String(a || '').split('?')[0];
    var nb = String(b || '').split('?')[0];
    return na === nb;
  }

  function resolveLoginUrl() {
    // Always return to the current PDP (path + query). Do not reuse Liquid return_to
    // which may omit ?variant= and break pending-save resume matching.
    return (
      window.location.origin +
      '/customer_authentication/login?return_to=' +
      encodeURIComponent(currentReturnPath())
    );
  }

  function normalizeProductId(id) {
    return String(id || '').replace(/\D/g, '');
  }

  function normalizeProductHandle(handle) {
    return String(handle || '').trim().toLowerCase();
  }

  function ingestSavedList(list, options) {
    options = options || {};
    if (!Array.isArray(list)) {
      savedIds.clear();
      savedHandles.clear();
      syncSavedUi();
      return;
    }
    if (
      list.length === 0 &&
      options.allowEmpty !== true &&
      (savedIds.size > 0 || savedHandles.size > 0)
    ) {
      return;
    }
    savedIds.clear();
    savedHandles.clear();
    list.forEach(function (row) {
      if (!row) return;
      var id = normalizeProductId(row.id || row.product_id);
      var handle = normalizeProductHandle(row.handle || row.product_handle);
      if (id) savedIds.add(id);
      if (handle) savedHandles.add(handle);
    });
    window.__muskleSavedProductsCache = list;
    syncSavedUi();
    document.dispatchEvent(
      new CustomEvent('muskle:saved-products-updated', {
        detail: { count: savedIds.size, saved: list },
      })
    );
  }

  function readBootstrapSaved() {
    var el = document.getElementById('MuskleSavedProductsBootstrap');
    if (!el) return;
    try {
      var parsed = JSON.parse(el.textContent || '[]');
      if (Array.isArray(parsed) && parsed.length > 0) {
        ingestSavedList(parsed);
      }
    } catch (e) {
      /* ignore */
    }
  }

  function probeBackend() {
    if (backendReady !== null) {
      return backendReady.then(function (ready) {
        return ready;
      });
    }
    backendReady = fetch(PROXY_PATH, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then(function (res) {
        return res
          .json()
          .catch(function () {
            return null;
          })
          .then(function (body) {
            var ready = res.ok && body && body.ok === true && body.enabled !== false;
            window.MuskleMemberFeatures.backendReady = ready;
            if (ready) {
              confirmMemberSessionLoggedIn();
              if (Array.isArray(body.saved)) {
                ingestSavedList(body.saved);
              }
            }
            return ready;
          });
      })
      .catch(function () {
        window.MuskleMemberFeatures.backendReady = false;
        return false;
      });
    return backendReady;
  }

  function loadSavedFromProxy() {
    if (savedProductsFetchInFlight) return savedProductsFetchInFlight;
    var gen = ++savedLoadGeneration;
    savedProductsFetchInFlight = fetch(PROXY_PATH, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then(function (res) {
        return res.json().catch(function () {
          return null;
        });
      })
      .then(function (body) {
        if (gen !== savedLoadGeneration) return false;
        if (body && body.ok) {
          confirmMemberSessionLoggedIn();
          if (Array.isArray(body.saved)) {
            ingestSavedList(body.saved, { allowEmpty: true });
          }
          window.MuskleMemberFeatures.backendReady = true;
          return true;
        }
        return false;
      })
      .catch(function () {
        return false;
      })
      .finally(function () {
        savedProductsFetchInFlight = null;
      });
    return savedProductsFetchInFlight;
  }

  function getActionContext(btn) {
    var actions = btn.closest('[data-muskle-member-actions]');
    return {
      action: btn.getAttribute('data-muskle-member-action') || '',
      loginUrl: btn.getAttribute('data-muskle-login-url') || '',
      liquidLoggedIn: isTruthy(btn.getAttribute('data-muskle-logged-in')),
      productId:
        btn.getAttribute('data-product-id') ||
        (actions && actions.getAttribute('data-product-id')) ||
        '',
      productHandle:
        btn.getAttribute('data-product-handle') ||
        (actions && actions.getAttribute('data-product-handle')) ||
        '',
      variantId:
        btn.getAttribute('data-variant-id') ||
        (actions && actions.getAttribute('data-variant-id')) ||
        '',
      root: actions || btn.closest('.muskle-card-save-heart-wrap') || btn.parentElement,
      btn: btn,
    };
  }

  function isProductSaved(productId, productHandle) {
    var id = normalizeProductId(productId);
    var handle = normalizeProductHandle(productHandle);
    return (id && savedIds.has(id)) || (handle && savedHandles.has(handle));
  }

  function setButtonSavedState(btn, saved) {
    if (!btn) return;
    var label = btn.querySelector('.muskle-member-actions__label');
    if (label) label.textContent = saved ? COPY.saved : COPY.save;
    btn.classList.toggle('muskle-member-actions__btn--saved', saved);
    btn.classList.toggle('muskle-card-save-heart--saved', saved);
    btn.setAttribute('data-muskle-is-saved', saved ? 'true' : 'false');
    btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
    if (btn.classList.contains('muskle-card-save-heart')) {
      btn.setAttribute(
        'aria-label',
        saved ? (isEnglish() ? 'Saved product' : 'Product bewaard') : isEnglish() ? 'Save product' : 'Bewaar product'
      );
    }
  }

  function syncSavedUi() {
    document.querySelectorAll('[data-muskle-member-action="save"]').forEach(function (btn) {
      var actions = btn.closest('[data-muskle-member-actions]');
      var id = normalizeProductId(
        btn.getAttribute('data-product-id') || (actions && actions.getAttribute('data-product-id'))
      );
      var handle = normalizeProductHandle(
        btn.getAttribute('data-product-handle') || (actions && actions.getAttribute('data-product-handle'))
      );
      if (!id && !handle) return;
      setButtonSavedState(btn, isProductSaved(id, handle));
    });
  }

  function scheduleSavedUiResync() {
    if (resyncScheduled) return;
    resyncScheduled = true;
    window.requestAnimationFrame(function () {
      resyncScheduled = false;
      syncSavedUi();
    });
  }

  function observeSavedStateResync() {
    if (savedStateObserver || !window.MutationObserver) return;
    savedStateObserver = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (!(node instanceof Element)) continue;
          if (
            node.matches('[data-muskle-member-action="save"]') ||
            node.querySelector('[data-muskle-member-action="save"]')
          ) {
            scheduleSavedUiResync();
            return;
          }
        }
      }
    });
    savedStateObserver.observe(document.body, { childList: true, subtree: true });
  }

  window.MuskleMemberFeatures.syncSavedUi = syncSavedUi;
  window.MuskleMemberFeatures.reloadSavedProducts = loadSavedFromProxy;

  function storePendingSave(ctx) {
    var productId = normalizeProductId(ctx && ctx.productId);
    if (!productId) return false;
    try {
      sessionStorage.setItem(
        PENDING_SAVE_KEY,
        JSON.stringify({
          productId: productId,
          productHandle: normalizeProductHandle(ctx.productHandle) || '',
          path: currentReturnPath(),
          ts: Date.now(),
        })
      );
      return true;
    } catch (e) {
      return false;
    }
  }

  function clearPendingSave() {
    try {
      sessionStorage.removeItem(PENDING_SAVE_KEY);
    } catch (e) {
      /* ignore */
    }
  }

  function readPendingSave() {
    try {
      var raw = sessionStorage.getItem(PENDING_SAVE_KEY);
      if (!raw) return null;
      var pending = JSON.parse(raw);
      if (!pending || !normalizeProductId(pending.productId)) return null;
      return pending;
    } catch (e) {
      return null;
    }
  }

  function probeLoggedIn(liquidLoggedIn) {
    if (liquidLoggedIn) return Promise.resolve(true);
    if (window.MuskleAccountSession && typeof window.MuskleAccountSession.probe === 'function') {
      return window.MuskleAccountSession.probe();
    }
    return Promise.resolve(false);
  }

  function findMessageRoot(root) {
    if (!root) return null;
    if (root.matches && root.matches('[data-muskle-member-actions]')) return root;
    if (root.classList && root.classList.contains('muskle-card-save-heart-wrap')) {
      var footer = root.closest('.product-card__muskle-footer');
      if (footer) return footer;
      var hero = root.closest('.muskle-product-hero');
      if (hero) return hero;
    }
    return root;
  }

  function clearInlineStatus(root) {
    var host = findMessageRoot(root);
    if (!host) return;
    var existing = host.querySelector('[data-muskle-member-status]');
    if (existing) existing.remove();
  }

  function showInlineStatus(root, html, isError) {
    var host = findMessageRoot(root);
    if (!host) return;

    var existing = host.querySelector('[data-muskle-member-status]');
    if (existing) existing.remove();

    var el = document.createElement('p');
    el.className = 'muskle-member-actions__status' + (isError ? ' muskle-member-actions__status--error' : '');
    el.setAttribute('data-muskle-member-status', '');
    el.setAttribute('role', 'status');
    el.innerHTML = html;
    host.appendChild(el);
  }

  function showGuestLoginPrompt(ctx) {
    var loginHref = escapeHtml(resolveLoginUrl(ctx.loginUrl));
    var html;
    if (isEnglish()) {
      html =
        '<a class="muskle-member-actions__login-link" href="' +
        loginHref +
        '">' +
        escapeHtml(COPY.loginLink) +
        '</a> to save products.';
    } else {
      html =
        '<a class="muskle-member-actions__login-link" href="' +
        loginHref +
        '">' +
        escapeHtml(COPY.loginLink) +
        '</a> om producten te bewaren.';
    }
    showInlineStatus(ctx.root, html, false);
  }

  function redirectGuestToLogin(ctx) {
    storePendingSave({
      action: 'save',
      productId: ctx.productId,
      productHandle: ctx.productHandle,
    });
    window.location.assign(resolveLoginUrl(ctx.loginUrl));
  }

  function mutateSavedProduct(ctx, forcedAction) {
    var productId = normalizeProductId(ctx.productId);
    var handle = normalizeProductHandle(ctx.productHandle);
    if (!productId && !handle) return Promise.resolve();
    var action =
      forcedAction || (isProductSaved(productId, handle) ? 'unsave' : 'save');
    var mutationGen = ++savedMutationGeneration;
    savedLoadGeneration = mutationGen;
    var errorCopy = action === 'unsave' ? COPY.unsaveError : COPY.saveError;

    return fetch(PROXY_PATH, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        product_id: productId,
        product_handle: handle || '',
        action: action,
      }),
    })
      .then(function (res) {
        return res.json().catch(function () {
          return null;
        });
      })
      .then(function (body) {
        if (mutationGen !== savedMutationGeneration) {
          return { ok: false, skipped: true };
        }
        if (!body || !body.ok) {
          showInlineStatus(ctx.root, escapeHtml(errorCopy), true);
          return { ok: false, body: body };
        }
        if (Array.isArray(body.saved)) {
          ingestSavedList(body.saved, { allowEmpty: true });
        } else if (action === 'unsave') {
          if (productId) savedIds.delete(productId);
          if (handle) savedHandles.delete(handle);
          syncSavedUi();
          renderHubSavedProducts();
        } else {
          if (productId) savedIds.add(productId);
          if (handle) savedHandles.add(handle);
          syncSavedUi();
          renderHubSavedProducts();
        }
        clearInlineStatus(ctx.root);
        return { ok: true, body: body };
      })
      .catch(function () {
        if (mutationGen !== savedMutationGeneration) {
          return { ok: false, skipped: true };
        }
        showInlineStatus(ctx.root, escapeHtml(errorCopy), true);
        return { ok: false };
      });
  }

  function toggleSavedProduct(ctx) {
    return mutateSavedProduct(ctx);
  }

  function showNotifyFuture(ctx) {
    void ctx;
  }

  function syncNotifyMeVariant(variantId) {
    var input = document.getElementById('restock-variant-change');
    if (!input) return;
    var next = variantId != null ? String(variantId) : '';
    if (!next) return;
    input.value = next;
    try {
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    } catch (err) {}
  }

  function openNotifyMeOfficialModal() {
    var autoBtn = document.querySelector('button.restock-alerts-notify-button');
    if (autoBtn) {
      try {
        autoBtn.click();
        return true;
      } catch (err) {}
    }
    return false;
  }

  function openNotifyPanel(btn) {
    var actions = btn.closest('[data-muskle-member-actions]');
    if (actions) {
      syncNotifyMeVariant(actions.getAttribute('data-variant-id') || '');
    }
    /* Official Notify Me! modal — not the retired MUSKLE inline form. */
    if (openNotifyMeOfficialModal()) return;
    showInlineStatus(
      actions || btn.parentElement,
      escapeHtml(
        isEnglish()
          ? 'Stock alerts are loading. Please try again in a moment.'
          : 'Voorraadmeldingen worden geladen. Probeer het zo opnieuw.'
      ),
      true
    );
  }

  function closeNotifyPanel(actions) {
    if (!actions) return;
    var btn = actions.querySelector('[data-muskle-member-action="notify"]');
    if (btn) btn.setAttribute('aria-expanded', 'false');
    var closeBtn = document.querySelector('.restock-alerts-modal-wrapper .modal-close-btn');
    if (closeBtn) {
      try {
        closeBtn.click();
      } catch (err) {}
    }
  }

  function submitStockNotify() {
    /* Retired: no Points stock-notify and no undocumented Notify Me API. */
    return Promise.resolve();
  }

  function handleAction(btn) {
    if (btn.getAttribute('data-muskle-member-busy') === 'true') return;
    btn.setAttribute('data-muskle-member-busy', 'true');

    var ctx = getActionContext(btn);

    if (ctx.action === 'notify') {
      openNotifyPanel(btn);
      btn.removeAttribute('data-muskle-member-busy');
      return;
    }

    probeLoggedIn(ctx.liquidLoggedIn)
      .then(function (loggedIn) {
        if (!loggedIn) {
          // Persist intended Save, then send guest through current Shopify login URL.
          redirectGuestToLogin(ctx);
          return;
        }

        return probeBackend().then(function (ready) {
          if (!ready) {
            // Authenticated UI probe passed, but App Proxy did not confirm saved-products.
            // Keep pending Save; show login/retry — never fake success.
            storePendingSave({
              action: 'save',
              productId: ctx.productId,
              productHandle: ctx.productHandle,
            });
            showGuestLoginPrompt(ctx);
            return;
          }
          return toggleSavedProduct(ctx);
        });
      })
      .finally(function () {
        btn.removeAttribute('data-muskle-member-busy');
      });
  }

  function processPendingSaveAfterLogin() {
    var pending = readPendingSave();
    if (!pending) return;

    if (pending.path && !sameProductPath(pending.path, currentReturnPath())) return;

    probeLoggedIn(false).then(function (loggedIn) {
      if (!loggedIn) return;

      return probeBackend().then(function (ready) {
        if (!ready) return;

        var productId = normalizeProductId(pending.productId);
        var productHandle = normalizeProductHandle(pending.productHandle);
        var selector =
          '[data-muskle-member-action="save"][data-product-id="' + productId + '"]';
        var btn = document.querySelector(selector);

        if (isProductSaved(productId, productHandle)) {
          clearPendingSave();
          syncSavedUi();
          return;
        }

        var root =
          (btn && btn.closest('[data-muskle-member-actions]')) ||
          (btn && btn.parentElement) ||
          document.querySelector('[data-muskle-member-actions]');

        return mutateSavedProduct(
          {
            action: 'save',
            productId: productId,
            productHandle: productHandle || '',
            root: root,
            btn: btn,
            loginUrl: (btn && btn.getAttribute('data-muskle-login-url')) || '',
            liquidLoggedIn: true,
            variantId: (btn && btn.getAttribute('data-variant-id')) || '',
          },
          'save'
        ).then(function (result) {
          if (result && result.ok) {
            clearPendingSave();
          }
        });
      });
    });
  }

  function initSavedProducts() {
    readBootstrapSaved();
    syncSavedUi();
    observeSavedStateResync();
    loadSavedFromProxy();
  }

  document.addEventListener('filter:update', function () {
    window.setTimeout(function () {
      syncSavedUi();
      if (document.documentElement.dataset.muskleMemberSessionConfirmed === 'true') {
        syncPointsSessionUi();
      }
    }, 0);
  });

  document.addEventListener('muskle:account-session', function (event) {
    if (event.detail && event.detail.loggedIn) {
      syncPointsSessionUi();
    }
  });

  window.addEventListener('pageshow', function (event) {
    if (event.persisted) loadSavedFromProxy();
  });

  document.addEventListener('click', function (event) {
    var btn = event.target.closest('[data-muskle-member-action]');
    if (btn) {
      event.preventDefault();
      event.stopPropagation();
      handleAction(btn);
      return;
    }

    var unsaveBtn = event.target.closest('[data-muskle-hub-unsave]');
    if (!unsaveBtn) return;
    event.preventDefault();
    if (unsaveBtn.getAttribute('data-muskle-member-busy') === 'true') return;
    unsaveBtn.setAttribute('data-muskle-member-busy', 'true');

    probeLoggedIn(true)
      .then(function (loggedIn) {
        if (!loggedIn) return;
        return mutateSavedProduct(
          {
            productId: unsaveBtn.getAttribute('data-product-id') || '',
            productHandle: unsaveBtn.getAttribute('data-product-handle') || '',
            root: unsaveBtn.closest('[data-muskle-saved-products-hub]'),
            btn: unsaveBtn,
            loginUrl: '',
            liquidLoggedIn: true,
            variantId: '',
          },
          'unsave'
        );
      })
      .finally(function () {
        unsaveBtn.removeAttribute('data-muskle-member-busy');
      });
  });

  document.addEventListener('submit', function (event) {
    var form = event.target.closest('[data-muskle-notify-form]');
    if (!form) return;
    event.preventDefault();
    event.stopPropagation();
  });

  document.addEventListener('muskle:variant-availability', function (event) {
    var detail = event.detail || {};
    var root = detail.root;
    var available = detail.available === true;
    var variantId = detail.variantId != null ? String(detail.variantId) : '';
    if (!root) return;
    if (variantId) syncNotifyMeVariant(variantId);
    root.querySelectorAll('[data-muskle-notify-wrap]').forEach(function (wrap) {
      if (available) {
        wrap.setAttribute('hidden', '');
        var actions = wrap.closest('[data-muskle-member-actions]') || root;
        closeNotifyPanel(actions);
      } else {
        wrap.removeAttribute('hidden');
      }
    });
    root.querySelectorAll('[data-muskle-member-actions]').forEach(function (actions) {
      actions.setAttribute('data-variant-available', available ? 'true' : 'false');
      if (variantId) actions.setAttribute('data-variant-id', variantId);
    });
  });

  /* Seed Notify Me! variant listener from current PDP selection. */
  (function seedNotifyMeVariant() {
    var actions = document.querySelector('[data-muskle-member-actions][data-variant-id]');
    if (actions) syncNotifyMeVariant(actions.getAttribute('data-variant-id'));
  })();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initSavedProducts();
      processPendingSaveAfterLogin();
      renderHubSavedProducts();
    });
  } else {
    initSavedProducts();
    processPendingSaveAfterLogin();
    renderHubSavedProducts();
  }

  function formatProductPrice(cents) {
    var amount = Number(cents);
    if (!Number.isFinite(amount)) return '';
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(amount);
    }
    return '€' + (amount / 100).toFixed(2).replace('.', ',');
  }

  function buildSavedEntries() {
    var entries = [];
    var seenIds = new Set();
    var seenHandles = new Set();

    if (window.__muskleSavedProductsCache && Array.isArray(window.__muskleSavedProductsCache)) {
      window.__muskleSavedProductsCache.forEach(function (row) {
        var pid = normalizeProductId(row && (row.id || row.product_id));
        var handle = normalizeProductHandle(row && (row.handle || row.product_handle));
        if ((!pid && !handle) || !isProductSaved(pid, handle)) return;
        if (pid && seenIds.has(pid)) return;
        if (handle && seenHandles.has(handle)) return;
        if (pid) seenIds.add(pid);
        if (handle) seenHandles.add(handle);
        entries.push({ id: pid, handle: handle });
      });
    }

    savedIds.forEach(function (id) {
      if (seenIds.has(id)) return;
      seenIds.add(id);
      entries.push({ id: id, handle: '' });
    });

    savedHandles.forEach(function (handle) {
      if (seenHandles.has(handle)) return;
      seenHandles.add(handle);
      var hasId = entries.some(function (entry) {
        return entry.handle === handle;
      });
      if (!hasId) entries.push({ id: '', handle: handle });
    });

    document.querySelectorAll('[data-muskle-member-action="save"]').forEach(function (btn) {
      var pid = normalizeProductId(btn.getAttribute('data-product-id'));
      var handle = normalizeProductHandle(btn.getAttribute('data-product-handle'));
      if (!pid || !savedIds.has(pid) || !handle) return;
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].id === pid) entries[i].handle = handle;
        else if (!entries[i].id && entries[i].handle === handle) entries[i].id = pid;
      }
    });

    return entries;
  }

  function fetchProductByHandle(handle) {
    if (!handle) return Promise.resolve(null);
    return fetch('/products/' + encodeURIComponent(handle) + '.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .catch(function () {
        return null;
      });
  }

  function buildProductUrl(entry, product) {
    var handle = (product && product.handle) || entry.handle || '';
    return handle ? '/products/' + encodeURIComponent(handle) : '';
  }

  function renderHubSavedProducts() {
    var hub = document.querySelector('[data-muskle-saved-products-hub]');
    if (!hub) return;
    var list = hub.querySelector('[data-muskle-saved-products-list]');
    var empty = hub.querySelector('[data-muskle-saved-products-empty]');
    if (!list) return;

    var entries = buildSavedEntries();
    if (!entries.length) {
      list.innerHTML = '';
      if (empty) {
        empty.textContent = COPY.hubEmpty;
        empty.hidden = false;
      }
      return;
    }
    if (empty) empty.hidden = true;

    var token = ++hubRenderToken;

    Promise.all(
      entries.map(function (entry) {
        if (!entry.handle) {
          return Promise.resolve({ entry: entry, product: null });
        }
        return fetchProductByHandle(entry.handle).then(function (product) {
          return { entry: entry, product: product };
        });
      })
    ).then(function (rows) {
      if (token !== hubRenderToken) return;

      list.innerHTML = '';

      rows.forEach(function (row) {
        var entry = row.entry;
        var product = row.product;
        var productUrl = buildProductUrl(entry, product);
        var li = document.createElement('li');
        li.className = 'muskle-member-hub__saved-item';

        var card = document.createElement('article');
        card.className = 'muskle-member-hub__saved-card';

        var cardMain = document.createElement('div');
        cardMain.className = 'muskle-member-hub__saved-card-main';

        if (product && (product.featured_image || (product.images && product.images[0]))) {
          var img = document.createElement('img');
          img.className = 'muskle-member-hub__saved-card-image';
          img.src = product.featured_image || product.images[0];
          img.alt = product.title || '';
          img.loading = 'lazy';
          img.width = 72;
          img.height = 72;
          cardMain.appendChild(img);
        }

        var body = document.createElement('div');
        body.className = 'muskle-member-hub__saved-card-body';

        var title = document.createElement('span');
        title.className = 'muskle-member-hub__saved-card-title';
        if (product && product.title) {
          title.textContent = product.title;
        } else if (entry.handle) {
          title.textContent = entry.handle.replace(/-/g, ' ');
        } else {
          title.textContent = 'Product #' + entry.id;
        }
        body.appendChild(title);

        if (product) {
          var price = document.createElement('span');
          price.className = 'muskle-member-hub__saved-card-price';
          price.textContent = formatProductPrice(
            product.price || (product.variants && product.variants[0] && product.variants[0].price)
          );
          body.appendChild(price);
        }

        cardMain.appendChild(body);
        card.appendChild(cardMain);

        var actions = document.createElement('div');
        actions.className = 'muskle-member-hub__saved-actions';

        if (productUrl) {
          var viewLink = document.createElement('a');
          viewLink.className = 'muskle-member-hub__saved-view';
          viewLink.href = productUrl;
          viewLink.textContent = COPY.viewProduct;
          actions.appendChild(viewLink);
        }

        var removeBtn = document.createElement('button');
        removeBtn.type = 'button';
        removeBtn.className = 'muskle-member-hub__saved-remove';
        removeBtn.setAttribute('data-muskle-hub-unsave', '');
        removeBtn.setAttribute('data-product-id', entry.id);
        removeBtn.setAttribute(
          'data-product-handle',
          entry.handle || (product && product.handle) || ''
        );
        removeBtn.setAttribute('aria-label', COPY.removeSaved);
        removeBtn.textContent = COPY.removeSaved;
        actions.appendChild(removeBtn);

        card.appendChild(actions);
        li.appendChild(card);
        list.appendChild(li);
      });
    });
  }

  document.addEventListener('muskle:saved-products-updated', function () {
    renderHubSavedProducts();
  });
})();